import "normalize.css"
import "../styles/style.css"

import { useLocation } from "@gatsbyjs/reach-router"
import PropTypes from "prop-types"
import React, { useState } from "react"
import Helmet from "react-helmet"
import { InstantSearch } from "react-instantsearch-dom"
import styled, { css, ThemeProvider } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import { px } from "styled-components-spacing/dist/cjs"

import config from "../settings/siteConfig"
import theme from "../settings/theme"
import { Footer } from "./Footer"
import { Menu } from "./Menu"
import { Results, searchStateToUrl, urlToSearchState } from "./search/Search"
import { searchClient } from "./search/searchClient"
import { SideBar, SIDEBAR_BREAKPOINT } from "./SideBar"

const PaddingWrapper = styled.div`
  ${px(1)};

  ${breakpoint("sm")(
    () =>
      css`
        padding-left: 20px;
        padding-right: 20px;
      `
  )}

  ${breakpoint(SIDEBAR_BREAKPOINT)(
    () =>
      css`
        padding-left: 20px;
        padding-right: 40px;
      `
  )}
`

const MainLayout = styled.section`
  display: grid;
  grid-template-columns: [main] 1fr;
  grid-column-gap: 40px;

  ${breakpoint("lg")(
    () => css`
      grid-template-columns: [menu] ${props => props.theme.sideBarWidth}px [main] 1fr;
    `
  )}

  >.main {
    grid-area: main;
    //width: 100%;
    /* overflow: auto; */
    max-width: 100%;
    margin: 0 auto;
    ${breakpoint("sm")(
      () =>
        css`
          width: calc(100vw - 40px); //20px + 20px padding from wrapper
        `
    )}

    ${breakpoint(SIDEBAR_BREAKPOINT)(
      () =>
        css`
          max-width: 1300px;
          min-height: calc(100vh - 64px);
          position: relative;
          padding-bottom: 141px;
          width: calc(
            100vw - ${props => props.theme.sideBarWidth}px - 60px - 40px
          ); // 20 + 40 padding + 40 grid gap
        `
    )}
  }
`

const Layout = props => {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const [searchState, setSearchState] = useState(urlToSearchState(location))
  const [debouncedSetState, setDebouncedSetState] = useState(null)

  const onSearchStateChange = updatedSearchState => {
    clearTimeout(debouncedSetState)

    setDebouncedSetState(
      setTimeout(() => {
        window.history.pushState(
          updatedSearchState || "none",
          null,
          searchStateToUrl(location, updatedSearchState)
        )
      }, 300)
    )
    setSearchState(updatedSearchState)
  }

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <link rel="preconnect" href="https://fast.karbonhq.com/" />
        <title>{config.siteTitle}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="preload"
          href="https://fast.karbonhq.com/osano/osano.min.css"
          as="style"
          onLoad="this.onload=null;this.rel='stylesheet'"
        />
        {/* Visually hide cookie widget  */}
        <style type="text/css">
          {`
          body .osano-cm-window__widget {
            opacity: 0;
            visibility: hidden;
          }
        `}
        </style>
      </Helmet>
      <PaddingWrapper>
        <InstantSearch
          searchClient={searchClient}
          indexName={process.env.GATSBY_ALGOLIA_INDEX}
          searchState={searchState}
          onSearchStateChange={onSearchStateChange}
        >
          <Menu
            hideSubBar={props.hideSubBar}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            backButton={props.backButton}
            title={props.menuTitle}
            searchTitle={
              searchState.query && `Search for “${searchState.query}”`
            }
            slug={props.slug}
          />
          <MainLayout>
            <SideBar
              hideRegionSelector={props.hideRegionSelector}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
            />
            <div className={"main"}>
              <Results>
                <main>{props.children}</main>
                <Footer hideDiscoverButton={props.hideDiscoverButton} />
              </Results>
            </div>
          </MainLayout>
        </InstantSearch>
      </PaddingWrapper>
    </ThemeProvider>
  )
}

Layout.propTypes = {
  hideRegionSelector: PropTypes.bool,
  hideSubBar: PropTypes.bool,
  hideDiscoverButton: PropTypes.bool,
  children: PropTypes.node.isRequired,
  slug: PropTypes.string,
  menuTitle: PropTypes.string,
  backButton: PropTypes.node,
}

export default Layout
